import React, { useEffect, useState, useRef } from 'react';
import { Room } from 'colyseus.js';

interface ChatProps {
    room: Room | null;
}

interface ChatMessage {
    id: string;
    sender: string;
    text: string;
    timestamp: number;
}

export const Chat: React.FC<ChatProps> = ({ room }) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const adminToken = import.meta.env.VITE_ADMIN_TOKEN as string | undefined;

    useEffect(() => {
        if (!room) {
            setIsConnected(false);
            return;
        }

        console.log("Chat: Connected to room", room.sessionId);
        setIsConnected(true);

        // Listen for chat messages
        const detachChatListener = room.onMessage("chat", (message: any) => {
            console.log("Chat received:", message);
            setMessages(prev => [...prev, {
                id: Math.random().toString(36).slice(2, 11),
                sender: message.sessionId,
                text: message.text,
                timestamp: Date.now()
            }]);
        });

        return () => {
            if (typeof detachChatListener === 'function') {
                detachChatListener();
            }
        };
    }, [room]);

    useEffect(() => {
        // Auto-scroll to bottom
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isOpen]);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        // Prevent typing from triggered WASD or Jump
        e.stopPropagation();

        if (e.key === 'Enter') {
            if (inputValue.trim() && room) {
                const text = inputValue.trim();

                // Admin Commands
                if (text.startsWith('/kick ')) {
                    const targetId = text.split(' ')[1];
                    if (targetId) {
                        if (!adminToken) {
                            setMessages(prev => [...prev, {
                                id: Math.random().toString(),
                                sender: "SYSTEM",
                                text: "Kick rejected: admin token not configured.",
                                timestamp: Date.now()
                            }]);
                            return;
                        }

                        room.send("kick", { targetId, adminToken });
                        setInputValue("");
                        // System feedback
                        setMessages(prev => [...prev, {
                            id: Math.random().toString(),
                            sender: "SYSTEM",
                            text: `Attempting to kick ${targetId}...`,
                            timestamp: Date.now()
                        }]);
                    }
                    return;
                }

                // Profanity Filter
                const badWords = ["bad", "evil", "rude"]; // Simple example list
                let filteredText = text;
                const regex = new RegExp(`\\b(${badWords.join("|")})\\b`, "gi");
                filteredText = filteredText.replace(regex, "****");

                room.send("chat", { text: filteredText });
                setInputValue("");
            }
        }
    };

    // Stop propagation on focus to prevent game inputs

    if (!room) return null;

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                style={{
                    position: 'absolute',
                    bottom: '20px',
                    left: '20px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    zIndex: 2000,
                    backdropFilter: 'blur(5px)'
                }}
            >
                Start Chat
            </button>
        );
    }

    return (
        <div style={{
            position: 'absolute',
            bottom: '20px',
            left: '20px',
            width: '320px',
            height: '240px',
            backgroundColor: 'rgba(10, 10, 15, 0.7)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            padding: '12px',
            zIndex: 2000,
            color: 'white',
            fontFamily: "'Inter', sans-serif",
            pointerEvents: 'auto',
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
        }}>
            {/* Header */}
            <div style={{ marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', opacity: 0.7 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: isConnected ? '#4f4' : '#f44',
                        boxShadow: `0 0 5px ${isConnected ? '#4f4' : '#f44'}`
                    }} />
                    <span>Global Chat</span>
                </div>
                <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
            </div>

            {/* Messages Area */}
            <div style={{
                flex: 1,
                overflowY: 'auto',
                marginBottom: '10px',
                fontSize: '13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                userSelect: 'text'
            }}>
                {messages.map(msg => (
                    <div key={msg.id} style={{ lineHeight: 1.4 }}>
                        <span style={{ color: msg.sender === room.sessionId ? '#4af' : '#2f9', fontWeight: 600 }}>
                            {msg.sender === room.sessionId ? 'You' : msg.sender.substr(0, 4)}:
                        </span>
                        <span style={{ marginLeft: '6px', color: 'rgba(255,255,255,0.9)' }}>{msg.text}</span>
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type a message..."
                style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    color: 'white',
                    padding: '8px 12px',
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    fontSize: '13px'
                }}
            />
        </div>
    );
};
